import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { IconButton, Modal, Typography } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { makeStyles } from '@material-ui/styles';
import ActionButton from '../../../components/ActionButton';
import DialogConfirmOperation from '../components/DialogConfirmOperation';
import ReminderFormFields, {
  ReminderDescription,
  ReminderRemoveConfirmDialog,
} from './ReminderFormFields';
import { REMINDER_STYLES } from './reminderConstants';
import { isReminderComplete, isReminderEmpty } from './reminderUtils';

const useStyles = makeStyles((theme) => ({
  modal: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    backgroundColor: 'white',
    color: 'black',
    borderRadius: '6px',
    padding: '30px',
    boxShadow: theme.shadows[6],
    width: '70%',
    maxWidth: '1200px',
    maxHeight: '90vh',
    overflowY: 'auto',
    [theme.breakpoints.down('md')]: {
      width: '90%',
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(4),
    marginBottom: theme.spacing(2),
  },
  closeButton: {
    border: `1px solid ${theme.palette.divider}`,
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerRight: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto',
  },
}));

const ReminderModal = ({
  onClose,
  onRemove,
  onSave,
  open,
  reminderMessage,
  reminderStyle,
}) => {
  const classes = useStyles();

  const [draftStyle, setDraftStyle] = useState(null);
  const [draftMessage, setDraftMessage] = useState('');
  const [isConfirmRemoveOpen, setIsConfirmRemoveOpen] = useState(false);
  const [isConfirmCloseOpen, setIsConfirmCloseOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setDraftStyle(reminderStyle ?? null);
      setDraftMessage(reminderMessage ?? '');
      setIsConfirmRemoveOpen(false);
      setIsConfirmCloseOpen(false);
    }
  }, [open, reminderStyle, reminderMessage]);

  const isDirty =
    draftStyle !== (reminderStyle ?? null) ||
    draftMessage !== (reminderMessage ?? '');

  const isSaveDisabled = !isReminderComplete(draftStyle, draftMessage);

  const handleRequestClose = () => {
    if (isDirty) {
      setIsConfirmCloseOpen(true);
      return;
    }
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        // Never lose in-progress edits to an accidental backdrop click; the
        // dirty guard in handleRequestClose only applies to explicit dismissals.
        if (reason === 'backdropClick') {
          return;
        }
        handleRequestClose();
      }}
      className={classes.modal}
    >
      <div className={classes.modalContainer}>
        <div>
          <div className={classes.header}>
            <Typography variant="h5">Promemoria</Typography>
            <IconButton
              aria-label="Chiudi"
              className={classes.closeButton}
              onClick={handleRequestClose}
              size="small"
            >
              <Close />
            </IconButton>
          </div>
          <ReminderDescription />
        </div>

        <ReminderFormFields
          reminderStyle={draftStyle}
          reminderMessage={draftMessage}
          onStyleChange={setDraftStyle}
          onMessageChange={setDraftMessage}
        />

        <div className={classes.footer}>
          {!isReminderEmpty(reminderStyle, reminderMessage) && (
            <ActionButton
              label="RIMUOVI"
              secondaryButton
              disableMargin={true}
              onClick={() => setIsConfirmRemoveOpen(true)}
            />
          )}
          <div className={classes.footerRight}>
            <ActionButton
              label="ANNULLA"
              secondaryButton
              onClick={handleRequestClose}
            />
            <ActionButton
              label="SALVA"
              disabled={isSaveDisabled}
              disableMargin={true}
              onClick={() => onSave(draftStyle, draftMessage.trim())}
            />
          </div>
        </div>

        {isConfirmRemoveOpen && (
          <ReminderRemoveConfirmDialog
            open={isConfirmRemoveOpen}
            onCancel={() => setIsConfirmRemoveOpen(false)}
            onConfirm={() => {
              setIsConfirmRemoveOpen(false);
              onRemove();
            }}
          />
        )}

        {isConfirmCloseOpen && (
          <DialogConfirmOperation
            open={isConfirmCloseOpen}
            title="Sei sicuro di voler chiudere?"
            description="Se lo fai, perderai tutte le informazioni non salvate finora."
            cancelButtonLabel="Annulla"
            confirmButtonLabel="Chiudi senza salvare"
            onCancel={() => setIsConfirmCloseOpen(false)}
            onConfirm={() => {
              setIsConfirmCloseOpen(false);
              onClose();
            }}
          />
        )}
      </div>
    </Modal>
  );
};

ReminderModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  reminderMessage: PropTypes.string,
  reminderStyle: PropTypes.oneOf([
    ...REMINDER_STYLES.map((item) => item.value),
    null,
  ]),
};

export default ReminderModal;
